import type { CityConfig } from "../city-template/types";

export const pomonaConfig: CityConfig = {
  city: "Pomona",
  state: "California",
  stateAbbr: "CA",
  slug: "pomona",
  route: "/cities/pomona",
  title: "Emergency Passport Pomona, CA | Fast Passport Renewal & Expedited Help",
  metaDescription: "Emergency passport and expedited passport services in Pomona, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for the Pomona Valley and eastern Los Angeles County. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Pomona, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Pomona and eastern Los Angeles County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Pomona%20California%20cityscape%20with%20the%20historic%20downtown%20district%20and%20Los%20Angeles%20County%20Fairplex%20visible%2C%20the%20majestic%20San%20Gabriel%20Mountains%20in%20the%20background%2C%20warm%20golden%20hour%20sunset%20light%20over%20the%20Pomona%20Valley%2C%20urban-suburban%20mix%20with%20tree-lined%20streets%20and%20classic%20architecture%2C%20professional%20landscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20warm%20earth%20tones%20with%20mountain%20backdrop%20and%20amber%20sky&width=1800&height=700&seq=pomona-hero-001&orientation=landscape",
  heroImageAlt: "Pomona California cityscape with historic downtown and San Gabriel Mountains at golden hour",
  geoPosition: "34.0551;-117.7490",
  geoPlacename: "Pomona, California",
  regionalAgency: {
    name: "Los Angeles Passport Agency",
    address: "300 N Los Angeles St, Los Angeles, CA 90012",
    distance: "Located in downtown LA — approximately 35–50 minutes from Pomona via I-10",
  },
  nearbyCitiesZips: [
    { name: "Pomona", zips: "91766, 91767, 91768" },
    { name: "Claremont", zips: "91711" },
    { name: "La Verne", zips: "91750" },
    { name: "San Dimas", zips: "91773" },
    { name: "Glendora", zips: "91740, 91741" },
    { name: "Covina", zips: "91722, 91723, 91724" },
    { name: "West Covina", zips: "91790, 91791, 91792" },
    { name: "Diamond Bar", zips: "91765" },
    { name: "Walnut", zips: "91789, 91788" },
    { name: "Chino", zips: "91708, 91710" },
    { name: "Chino Hills", zips: "91709" },
    { name: "Rancho Cucamonga", zips: "91701, 91730, 91737, 91739" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent eastern LA County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Los Angeles", route: "/cities/los-angeles" },
    { name: "Pasadena", route: "/cities/pasadena" },
    { name: "Rancho Cucamonga", route: "/cities/rancho-cucamonga" },
    { name: "Anaheim", route: "/cities/anaheim" },
    { name: "Riverside", route: "/cities/riverside" },
    { name: "Ontario", route: "/cities/ontario" },
    { name: "Burbank", route: "/cities/burbank" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Pomona remotely via secure courier. We do not have a walk-in office in Pomona — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Pomona Valley travelers.",
  fpcOfficeAddress: "225 S Lake Ave, Pasadena, CA 91101",
  fpcOfficeDistance: "Serving the Pomona Valley and eastern Los Angeles County",
  serviceAreaIntro: "From the historic downtown district to the Cal Poly Pomona campus, from the Los Angeles County Fairplex to the Pomona Arts Colony — Fast Passport Center serves the entire Pomona community. We regularly help travelers from Claremont, La Verne, San Dimas, and Diamond Bar get their passports fast.",
  nearbyCitiesIntro: "We serve all Pomona neighborhoods and nearby eastern LA County cities including Claremont, Glendora, Covina, West Covina, Walnut, and Diamond Bar. If you are in the Pomona Valley or San Gabriel Valley foothills, we have you covered.",
  howItWorksIntro: "Most Pomona travelers complete everything online and ship documents overnight. No need to drive to Los Angeles or wait in line at the regional agency.",
  mistakesIntro: "With LAX and Ontario International serving as the closest international gateways, Pomona travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Pomona and eastern LA County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Pomona? Here are the most common ones we hear from Pomona Valley travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Pomona Valley international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "David W.",
      location: "Pomona, CA",
      rating: 5,
      text: "My business trip to Singapore was in 2 days and my passport was expired. Fast Passport Center got me a new one in 36 hours. Saved my trip completely.",
      service: "Emergency Passport",
    },
    {
      name: "Sarah M.",
      location: "Claremont, CA",
      rating: 5,
      text: "Needed to renew for a family vacation to Japan. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "James R.",
      location: "Glendora, CA",
      rating: 5,
      text: "Lost my passport the week before my honeymoon to Spain. They walked me through the DS-64 process and got me a replacement in 48 hours. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Emily K.",
      location: "La Verne, CA",
      rating: 5,
      text: "As a frequent international traveler from the Pomona Valley, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "pomona-1",
      name: "Pomona Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "100 E 3rd St",
      city: "Pomona",
      state: "CA",
      zip: "91766",
      phone: "(909) 622-3122",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Pomona",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "pomona-2",
      name: "Claremont Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "140 N Indian Hill Blvd",
      city: "Claremont",
      state: "CA",
      zip: "91711",
      phone: "(909) 626-3846",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "2.5 miles from Claremont McKenna College",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "pomona-3",
      name: "West Covina Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1000 W Covina Pkwy",
      city: "West Covina",
      state: "CA",
      zip: "91790",
      phone: "(626) 919-3382",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.2 miles from West Covina Plaza",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular location for eastern LA County residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "pomona-4",
      name: "Rancho Cucamonga Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "10801 E Foothill Blvd",
      city: "Rancho Cucamonga",
      state: "CA",
      zip: "91730",
      phone: "(909) 989-8214",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.8 miles from Victoria Gardens",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Inland Empire residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "pomona-5",
      name: "Diamond Bar Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "23747 S Pomona Blvd",
      city: "Diamond Bar",
      state: "CA",
      zip: "91765",
      phone: "(909) 861-2145",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.0 miles from Diamond Bar High School",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for eastern LA County residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "pomona-6",
      name: "LA County Clerk — Norwalk",
      type: "County Clerk Passport Acceptance",
      address: "12400 Imperial Hwy",
      city: "Norwalk",
      state: "CA",
      zip: "90650",
      phone: "(562) 462-2133",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "15 miles from downtown Pomona",
      website: "https://www.lavote.net/",
      websiteLabel: "LA County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown LA", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Pomona Valley photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Pomona Valley acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Pomona Valley applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the LA regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Pomona Valley families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Pomona Valley travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Pomona." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Pomona address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Pomona before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Pomona for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most San Gabriel Valley travelers." },
    { q: "How fast can I renew my passport in Pomona?", a: "Fast Passport Center offers fast passport renewal in Pomona in as little as 24–48 hours through our emergency courier service." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Pomona in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Pomona?", a: "Same-day passport appointments are only available at the LA Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Pomona residents." },
    { q: "Can I expedite a child passport in Pomona?", a: "Yes. Fast Passport Center expedites child passports in Pomona for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Pomona with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Pomona?",
      a: "With Fast Passport Center's emergency courier service, Pomona travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the Los Angeles Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Pomona?",
      a: "Popular options include the Pomona Post Office (91766), Claremont Post Office (91711), West Covina Post Office (91790), and Rancho Cucamonga Post Office (91730). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Pomona?",
      a: "Same-day passports are only available at the LA Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in Glendora, Covina, or West Covina?",
      a: "We serve all of the Pomona Valley and San Gabriel Valley foothills including Glendora, Covina, West Covina, San Dimas, and Walnut. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Pomona?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Pomona?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Pomona and Claremont offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Pomona?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Pomona?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Pomona Valley families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Pomona", address: "100 E Mission Blvd, Pomona, CA 91766", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Claremont", address: "665 W Foothill Blvd, Claremont, CA 91711", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — West Covina", address: "1000 W Covina Pkwy, West Covina, CA 91790", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Glendora", address: "135 W Route 66, Glendora, CA 91740", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Chino Hills", address: "3943 Grand Ave, Chino Hills, CA 91709", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — La Verne", address: "2033 Foothill Blvd, La Verne, CA 91750", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Diamond Bar", address: "23747 S Pomona Blvd, Diamond Bar, CA 91765", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Rancho Cucamonga", address: "12530 E Foothill Blvd, Rancho Cucamonga, CA 91739", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Pomona",
      address: "1600 S Garey Ave, Pomona, CA 91766",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — West Covina",
      address: "1000 W Covina Pkwy, West Covina, CA 91790",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Claremont",
      address: "665 W Foothill Blvd, Claremont, CA 91711",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — San Bernardino",
      address: "655 E 3rd St, San Bernardino, CA 92415",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for San Bernardino County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "ONT",
      name: "Ontario International Airport",
      distance: "10 miles from Pomona",
      address: "1923 E Avion St, Ontario, CA 91761",
      note: "The closest major airport to Pomona. International flights to Mexico and connections to Asia and Europe.",
      flightUrl: "https://www.flyontario.com/",
      flightLabel: "Ontario Airport",
      featured: true,
    },
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "35 miles from Pomona",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "Major international hub for long-haul flights to Asia, Europe, and South America. Accessible via I-10.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Flight Info",
      featured: false,
    },
    {
      code: "BUR",
      name: "Hollywood Burbank Airport",
      distance: "40 miles from Pomona",
      address: "2627 N Hollywood Way, Burbank, CA 91505",
      note: "Domestic flights and some international connections. Less crowded than LAX.",
      flightUrl: "https://www.hollywoodburbankairport.com/",
      flightLabel: "Burbank Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    {
      label: "California Dept. of Public Health (Vital Records)",
      description: "State-issued birth and death certificates for all California counties",
      url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx",
    },
    {
      label: "LA County Registrar-Recorder (Birth, Death, Marriage Records)",
      description: "Birth certificates, death certificates, and marriage records for Los Angeles County",
      url: "https://www.lavote.gov/records/",
    },
    {
      label: "LA County Superior Court (Divorce Records)",
      description: "Divorce decrees and court records for Los Angeles County",
      url: "https://www.lacourt.org/",
    },
  ],
};